import { assert } from '@deqo/foundation';
import { getter } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { roundClamp, toDegrees } from '../../../platform/geom/Math';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { KeyInput } from '../../../platform/input/KeyInput';
import { Keys } from '../../../platform/input/Keys';
import { Modifiers } from '../../../platform/input/Modifiers';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { Colors } from '../../../ui';
import { Activator, resolve } from '../../Container';
import { AutoLayoutEngine } from '../../packages/autoLayout/AutoLayoutEngine';
import { Camera } from '../Camera';
import { Cursor, CursorValue } from '../Cursor';
import { EditorSession } from '../EditorSession';
import { InteractionHelper } from '../InteractionHelper';
import { PointerInfo } from '../PointerInfo';
import { Selection, SelectionWatch } from '../Selection';
import { bringForward, bringToFront, sendBackward, sendToBack } from '../commands/OrderCommands';
import { cloneSelection, collectAtCursor } from '../commands/SelectionCommands';
import { move } from '../commands/TransformCommands';
import { RelativeTo } from '../entities/Physical';
import { NetRegister } from '../nets/NetRegister';
import { SpatialIndex } from '../utilities/SpatialIndex';
import { KeyMappings } from './KeyMappings';
import { ToolBehavior, ToolSettings } from './ToolBehavior';
import { ToolOverlay } from './ToolOverlay';
import { SelectionRotateTransformation } from './transform/SelectionRotateTransformation';
import { SelectionScaleTransformation } from './transform/SelectionScaleTransformation';
import { SelectionSwapTransformation } from './transform/SelectionSwapTransformation';
import { SelectionTransformation } from './transform/SelectionTransformation';
import { SelectionTranslateTransformation } from './transform/SelectionTranslateTransformation';
import { TransformNet } from './transform/TransformNet';
import { Transformation, TransformationInput } from './transform/Transformation';

const normalizeAngle = (angle: number = 0) => {
    angle = roundClamp(toDegrees(angle), 5);
    if (angle > 180) angle -= 360;
    if (angle < -180) angle += 360;
    return angle;
};

const rotateCursor = (angle: number = 0) => `rotate-${normalizeAngle(angle)}`;
const scaleCursor = (angle: number = 0) => `scale-${normalizeAngle(angle)}`;

// NOTE
// Lessons for when working in here:
//
//   1. Transformation has to always come from root level, you get too much weird shit otherwise.
//   2. Scale uses not the startPosition (mouse) but the handle position as the initialPosition.
//

export class TransformToolBehavior extends ToolBehavior {

    @resolve(AutoLayoutEngine) protected autoLayout!: AutoLayoutEngine;
    @resolve(Activator) protected activator!: Activator;
    @resolve(Camera) protected camera!: Camera;
    @resolve(CommandDispatcher) protected dispatch!: CommandDispatcher;
    @resolve(InteractionHelper) protected targeting!: InteractionHelper;
    @resolve(NetRegister) protected nets!: NetRegister;
    @resolve(ObjectManager) protected objects!: ObjectManager;
    @resolve(SpatialIndex) private spatial!: SpatialIndex;
    @resolve(ToolOverlay) private overlay!: ToolOverlay;
    @resolve(EditorSession) protected session!: EditorSession;

    private transformation: SelectionTransformation | null = null;
    private forceCursor: CursorValue | null = null;
    private watch: SelectionWatch;

    private primedPosition = null as Point;

    public init(_settings: ToolSettings, _param: string) {
        this.burden(
            this.overlay.add(this.paintSelection),
            // this.overlay.add(this.paintDrop),
            this.watch = new SelectionWatch(this.session, this.objects, () => {
                this.updateNet();
                this.overlay.repaint();
            }),
            () => this.updateNet(true),
            () => this.escape(),
        );
        this.updateNet();
    }

    public getKeyMappings = getter(KeyMappings.from(
        ['OPEN_BRACKET', sendBackward],
        ['OPEN_BRACKET+SHIFT', sendToBack],
        ['CLOSE_BRACKET', bringForward],
        ['CLOSE_BRACKET+SHIFT', bringToFront],
        ['LEFT_ARROW', [move, ['left', 'grid']]],
        ['LEFT_ARROW+CTRL', [move, ['left', 'unit']]],
        ['LEFT_ARROW+SHIFT', [move, ['left', 'grid10x']]],
        ['LEFT_ARROW+CTRL+SHIFT', [move, ['left', 'bounds']]],
        ['LEFT_ARROW+ALT', [cloneSelection, ['left']]],
        ['UP_ARROW', [move, ['up', 'grid']]],
        ['UP_ARROW+CTRL', [move, ['up', 'unit']]],
        ['UP_ARROW+SHIFT', [move, ['up', 'grid10x']]],
        ['UP_ARROW+CTRL+SHIFT', [move, ['up', 'bounds']]],
        ['UP_ARROW+ALT', [cloneSelection, ['up']]],
        ['RIGHT_ARROW', [move, ['right', 'grid']]],
        ['RIGHT_ARROW+CTRL', [move, ['right', 'unit']]],
        ['RIGHT_ARROW+SHIFT', [move, ['right', 'grid10x']]],
        ['RIGHT_ARROW+CTRL+SHIFT', [move, ['right', 'bounds']]],
        ['RIGHT_ARROW+ALT', [cloneSelection, ['right']]],
        ['DOWN_ARROW', [move, ['down', 'grid']]],
        ['DOWN_ARROW+CTRL', [move, ['down', 'unit']]],
        ['DOWN_ARROW+SHIFT', [move, ['down', 'grid10x']]],
        ['DOWN_ARROW+CTRL+SHIFT', [move, ['down', 'bounds']]],
        ['DOWN_ARROW+ALT', [cloneSelection, ['down']]],
    ));

    public getPreferredCursor(state: string, mi: MouseInput | KeyInput) {
        return this.selectNextCursorForTarget(mi.targetOverlay);
    }

    public onMouseDown(state: string, mi: MouseInput): string {
        if (state === 'idle' && mi.button === MouseButtons.Main) {
            if (this.shouldPrime(mi)) {
                this.prime(mi);
                return 'transformPrimed';
            }
        }
        if (state === 'transforming' && mi.button === MouseButtons.Secondary) {
            this.dispatch(collectAtCursor, ['next', false]);
        }
        return state;
    }

    public onMouseMove(state: string, mi: MouseInput): string {
        this.updateForcedCursorForTarget(mi.targetOverlay);
        if (state === 'transformPrimed') {
            // Synthetic transforms from other behaviors will mean transformPrimed without actual transformation
            if (!this.transformation) {
                this.prime(mi); // Prime from the current mi
                this.primedPosition = null; // Just to be safe, ensure this is null
            }
            if (!!this.transformation) {
                this.begin(this.toInput(mi, this.primedPosition));
                state = 'transforming';
            }
            else {
                state = 'idle';
            }
        }
        if (state === 'transforming') {
            this.continue(this.toInput(mi));
        }
        return state;
    }

    public onMouseEdge(state: string, mi: MouseInput): string {
        if (state === 'transforming') {
            const ef = this.selectEdgeFactor(null);
            const ev = this.camera.toEdgeVector(mi.position);
            if (ev.length() > 0) {
                const sv = ev.multiply(ef).multiply(this.camera.scale / this.camera.scale);
                this.camera.move(sv);
                this.continue(this.toInput(mi));
            }
        }
        return state;
    }

    public onMouseUp(state: string, mi: MouseInput): string {
        if (mi.button !== MouseButtons.Main) return;
        if (state === 'transformPrimed') {
            this.escape();
            // Double click on a scale
            // if (mi.count === 2) {
            //     this.maybeFitToEdge(mi);
            // }
            return 'idle';
        }
        if (state === 'transforming') {
            this.complete(this.toInput(mi));
            return 'idle';
        }
        return state;
    }

    public onKeyDown(state: string, ki: KeyInput): string {
        if (state === 'transforming' && Modifiers.is(ki.key)) {
            this.continue(this.toInput(ki));
        }
        if ((state === 'transforming' || state === 'transformPrimed') && ki.key === Keys.ESCAPE) {
            this.escape();
            return 'idle';
        }
        return state;
    }

    public onKeyUp(state: string, ki: KeyInput): string {
        if (state === 'transforming' && Modifiers.is(ki.key)) {
            this.continue(this.toInput(ki));
        }
        return state;
    }

    public onStateChanged(newState: string, oldState: string): void {
        // If we were transforming and another behavior stole the limelight, silently cleanup and don't undo anything as we should assume
        // they know what they are doing.
        if (oldState === 'transforming' && newState !== 'transforming') {
            this.escape();
        }

        if (newState === 'transforming') {
            this.watch.disable();
        }
        else {
            this.watch.enable();
        }
    }

    protected selectEdgeFactor(at: Transformation) {
        // if (at instanceof TranslateTransformation) return 50;
        // if (at instanceof ScaleTransformation) return 25;
        return 40;
    }

    private assertTransforming() {
        assert(!!this.transformation, 'TransformToolBehavior: transforming without activeTransformation?');
    }

    protected toInput(di: KeyInput | MouseInput, positionOverride?: Point) {
        const position = positionOverride ?? (('position' in di) ? di.position : this.session.get(PointerInfo).position);
        return {
            position: this.camera.toModel(position),
            modifiers: di.modifiers,
        };
    }

    protected shouldPrime(mi: MouseInput) {
        const selection = this.session.get(Selection);
        // Only consider selection if selection is not locked
        if (selection.some(x => x.locked)) {
            return false;
        }
        // Prime if main button targeting a control
        if (mi.button === MouseButtons.Main && mi.targetOverlay?.type === 'transform') {
            return true;
        }
        // Prime if targetting a selected physical
        if (mi.targets.some(x => selection.has(x))) {
            return true;
        }
        // Finally, prime if mouse within selection net, otherwise don't
        const net = this.nets.get('transform') as TransformNet;
        const mpt = this.camera.toModel(mi.position);
        return net?.verticies?.contains(mpt) ?? false;
    }

    protected prime(mi: MouseInput, opts: { updateKey?: string, override?: SelectionTransformation } = {}) {
        assert(!this.transformation, 'TransformToolBehavior: prime when already primed?');
        this.primedPosition = mi.position;
        const tm = this.transformation = (opts.override ?? this.createTransformationForInput(mi));
        tm.key = opts.updateKey || null;
        tm.on('commit', ([ids]) => this.objects.getActiveTriggerCycle().emit('@manipulated', ids, [tm.type]));
        return tm;
    }

    protected begin(input: TransformationInput) {
        this.assertTransforming();
        this.transformation.begin(input);
        this.updateNet();
        this.overlay.repaint();
    }

    protected continue(input: TransformationInput) {
        this.assertTransforming();
        this.transformation.continue(input);
        this.updateNet(this.transformation instanceof SelectionSwapTransformation);
        this.overlay.repaint(true);
    }

    protected complete(input: TransformationInput) {
        this.assertTransforming();
        this.transformation.complete(input);
        this.transformation = null;
        this.primedPosition = null;
        this.updateNet();
        this.overlay.repaint();
    }

    protected escape() {
        if (this.transformation) {
            this.transformation.destroy();
            this.transformation = null;
            this.primedPosition = null;
            this.updateNet();
            this.overlay.repaint();
        }
    }

    private createTransformationForInput(mi: MouseInput) {
        if (mi.targetOverlay?.type === 'transform') {
            const { impl, args } = (mi.targetOverlay as any).data;
            const inst = this.activator.call(impl) as any;
            inst.configure(args);
            return inst;
        }
        // No control transformation, default to translation...
        else {
            return this.activator.call(SelectionTranslateTransformation);
        }
    }

    private selectNextCursorForTarget(target: any) {
        const { transformation } = this;
        if (transformation instanceof SelectionTranslateTransformation) {
            return null;
        }
        if (transformation instanceof SelectionRotateTransformation) {
            return rotateCursor(transformation.angle + transformation.axis.angle());
        }
        if (transformation instanceof SelectionScaleTransformation) {
            return scaleCursor(transformation.angle + transformation.axis.angle());
        }
        if (target?.type === 'transform') {
            const { cursor } = (target.data ?? {}) as any;
            if (!!cursor) return cursor;
        }
        return null;
    }

    private updateForcedCursorForTarget(target: any) {
        this.forceCursor = this.selectNextCursorForTarget(target);
        if (this.forceCursor) {
            Cursor.set(this.forceCursor);
        }
    }

    private updateNet(forceDelete?: boolean) {
        const { session, nets } = this;
        const net = TransformNet.compute(this.camera, session.get(Selection).toArray());
        if (net && !forceDelete) {
            nets.set('transform', net);
        }
        else {
            nets.delete('transform');
        }
    }

    private paintSelection = (gfx: UniversalSurfaceRenderer) => {
        const camera = this.camera;
        const selection = this.session.get(Selection);
        if (selection.size <= 1) return;
        if (!selection.some(x => x.queryTransformHints(true).visible)) return;

        gfx.lineWidth = 1.5;
        gfx.strokeStyle = Colors.primaryLight;

        for (const p of selection) {
            const { visible } = p.queryTransformHints(false);
            if (!visible) continue;

            const rect = Rect.fromPoints(camera.toView(p.bounds(RelativeTo.Model)));
            gfx.strokeRect(rect.left, rect.top, rect.width, rect.height);
        }
    };

}
